import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import FundingGuide from '../components/FundingGuide';
import { 
  ArrowLeft, Key, Plus, Trash2, RefreshCw, 
  CheckCircle2, XCircle, Shield, ExternalLink,
  Eye, EyeOff, AlertTriangle, Loader2
} from 'lucide-react';

const APIKeyManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [keys, setKeys] = useState([]);
  const [supportedExchanges, setSupportedExchanges] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [formData, setFormData] = useState({
    exchange: 'kraken',
    api_key: '',
    api_secret: '',
    label: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [keysRes, exchangesRes] = await Promise.all([
        api.get('/api-keys'),
        api.get('/api-keys/supported-exchanges')
      ]);
      setKeys(keysRes.data.keys || []);
      setSupportedExchanges(exchangesRes.data.exchanges || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKey = async (e) => {
    e.preventDefault();
    
    if (!formData.api_key || !formData.api_secret) {
      toast.error('API Key and Secret are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post('/api-keys/add', formData);
      if (response.data.note) {
        toast.success(response.data.note);
      } else {
        toast.success('API key added and verified successfully!');
      }
      setShowAddForm(false);
      setFormData({ exchange: 'kraken', api_key: '', api_secret: '', label: '' });
      loadData();
    } catch (error) {
      console.error('Add key error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add API key');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!confirm('Are you sure you want to remove this API key?')) return;
    
    try {
      await api.delete(`/api-keys/${keyId}`);
      toast.success('API key removed');
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove API key');
    }
  };

  const handleTestKey = async (keyId) => {
    setTesting(keyId);
    try {
      const response = await api.post(`/api-keys/${keyId}/test`);
      if (response.data.valid) {
        toast.success('Connection successful!');
      } else {
        toast.error('Connection failed');
      }
      loadData();
    } catch (error) {
      console.error('Test error:', error);
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const getExchangeLogo = (exchange) => {
    const logos = {
      kraken: 'https://upload.wikimedia.org/wikipedia/commons/1/12/Kraken_logo.svg',
      coinbase: 'https://upload.wikimedia.org/wikipedia/commons/1/1a/Coinbase.svg'
    };
    return logos[exchange] || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F6BFF] mx-auto mb-4"></div>
          <p className="text-[#94A3B8]">Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Button
            data-testid="api-keys-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button
            data-testid="add-api-key-btn"
            onClick={() => setShowAddForm(true)}
            className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/80"
          >
            <Plus className="w-4 h-4 mr-2" /> Add API Key
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-4xl">
        {/* Page Title */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <Key className="w-8 h-8 text-[#2F6BFF]" />
            <h1 className="text-4xl font-semibold font-['Outfit']">API Key Manager</h1>
          </div>
          <p className="text-[#94A3B8]">Securely manage your exchange API keys for live trading</p>
        </motion.div>

        {/* Security Notice */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="bg-[#2F6BFF]/10 border-[#2F6BFF]/30">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-[#2F6BFF] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#2F6BFF] mb-1">Security Information</h3>
                  <ul className="text-sm text-[#94A3B8] space-y-1">
                    <li>• API keys are encrypted before storage</li>
                    <li>• Only enable trading permissions - never withdrawal permissions</li>
                    <li>• Use IP whitelist on your exchange for additional security</li>
                    <li>• We never see or store your actual keys in plain text</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Funding Guide */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <FundingGuide variant="full" />
        </motion.div>

        {/* Add Key Form Modal */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
              onClick={() => setShowAddForm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg"
              >
                <Card className="bg-[#15171C] border-[#1E293B]">
                  <CardHeader>
                    <CardTitle className="font-['Outfit']">Add Exchange API Key</CardTitle>
                    <CardDescription>Connect your exchange for live trading</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAddKey} className="space-y-4">
                      {/* Exchange Select */}
                      <div className="space-y-2">
                        <Label>Exchange</Label>
                        <select
                          value={formData.exchange}
                          onChange={(e) => setFormData({...formData, exchange: e.target.value})}
                          className="w-full bg-[#0B0D12] border border-[#1E293B] rounded-md px-3 py-2 text-[#F5F7FA]"
                        >
                          {supportedExchanges.map((ex) => (
                            <option key={ex.id} value={ex.id}>{ex.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Instructions Link */}
                      {supportedExchanges.find(e => e.id === formData.exchange) && (
                        <a
                          href={supportedExchanges.find(e => e.id === formData.exchange).docs_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#2F6BFF] flex items-center gap-1 hover:underline"
                        >
                          How to create API keys <ExternalLink className="w-3 h-3" />
                        </a>
                      )}

                      {/* Kraken-specific setup instructions */}
                      {formData.exchange === 'kraken' && (
                        <div className="p-4 rounded-lg bg-[#0B0D12] border border-[#1E293B]">
                          <h4 className="font-medium text-sm mb-2 text-[#2F6BFF]">Kraken API Setup Steps:</h4>
                          <ol className="text-xs text-[#94A3B8] space-y-1 list-decimal list-inside">
                            <li>Log in to Kraken and go to Settings → API</li>
                            <li>Click "Create New Key" and name it "Apexion Trading"</li>
                            <li>Complete 2FA verification</li>
                            <li>Enable "Create & Modify Orders" and "Query Open Orders & Trades"</li>
                            <li className="text-yellow-400 font-medium">Do NOT enable "Withdraw Funds"</li>
                            <li>Copy your API Key and Private Key</li>
                          </ol>
                        </div>
                      )}

                      {/* Label */}
                      <div className="space-y-2">
                        <Label>Label (optional)</Label>
                        <Input
                          placeholder="e.g., Trading Bot Key"
                          value={formData.label}
                          onChange={(e) => setFormData({...formData, label: e.target.value})}
                          className="bg-[#0B0D12] border-[#1E293B]"
                        />
                      </div>

                      {/* API Key */}
                      <div className="space-y-2">
                        <Label>API Key</Label>
                        <Input
                          type="text"
                          placeholder="Enter your API key"
                          value={formData.api_key}
                          onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                          className="bg-[#0B0D12] border-[#1E293B] font-mono text-sm"
                          required
                        />
                      </div>

                      {/* API Secret */}
                      <div className="space-y-2">
                        <Label>API Secret</Label>
                        <div className="relative">
                          <Input
                            type={showSecret ? "text" : "password"}
                            placeholder="Enter your API secret"
                            value={formData.api_secret}
                            onChange={(e) => setFormData({...formData, api_secret: e.target.value})}
                            className="bg-[#0B0D12] border-[#1E293B] font-mono text-sm pr-10"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecret(!showSecret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#F5F7FA]"
                          >
                            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Warning */}
                      <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-[#94A3B8]">
                            Only grant SPOT trading permissions. Never enable withdrawal permissions 
                            for API keys used with third-party applications.
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowAddForm(false)}
                          className="flex-1 border-[#1E293B]"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={saving}
                          className="flex-1 bg-[#2F6BFF] hover:bg-[#2F6BFF]/80"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Validating...
                            </>
                          ) : (
                            <>
                              <Key className="w-4 h-4 mr-2" />
                              Add Key
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* API Keys List */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          {keys.length === 0 ? (
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardContent className="py-12 text-center">
                <Key className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No API Keys Connected</h3>
                <p className="text-[#94A3B8] mb-4">
                  Add your exchange API keys to enable live trading
                </p>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/80"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Your First API Key
                </Button>
              </CardContent>
            </Card>
          ) : (
            keys.map((key) => (
              <Card key={key.id} className="bg-[#15171C] border-[#1E293B]">
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Exchange Logo */}
                      <div className="w-12 h-12 rounded-full bg-[#1E293B] flex items-center justify-center overflow-hidden">
                        {getExchangeLogo(key.exchange) ? (
                          <img 
                            src={getExchangeLogo(key.exchange)} 
                            alt={key.exchange}
                            className="w-8 h-8 object-contain"
                          />
                        ) : (
                          <Key className="w-6 h-6 text-[#94A3B8]" />
                        )}
                      </div>
                      
                      {/* Key Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{key.label || `${key.exchange.charAt(0).toUpperCase() + key.exchange.slice(1)} Key`}</h3>
                          {key.is_connected ? (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle2 className="w-3 h-3" /> Connected
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-red-400">
                              <XCircle className="w-3 h-3" /> Disconnected
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#94A3B8]">
                          {key.exchange.charAt(0).toUpperCase() + key.exchange.slice(1)} • 
                          Added {new Date(key.created_at).toLocaleDateString()}
                        </p>
                        {key.permissions && key.permissions.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {key.permissions.map((perm) => (
                              <span 
                                key={perm}
                                className="text-xs px-2 py-0.5 rounded bg-[#2F6BFF]/20 text-[#2F6BFF]"
                              >
                                {perm}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestKey(key.id)}
                        disabled={testing === key.id}
                        className="border-[#1E293B]"
                      >
                        {testing === key.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteKey(key.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </motion.div>

        {/* Supported Exchanges */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Supported Exchanges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {supportedExchanges.map((ex) => (
                  <div 
                    key={ex.id}
                    className="p-4 rounded-lg bg-[#0B0D12] border border-[#1E293B]"
                  >
                    <h4 className="font-medium mb-1">{ex.name}</h4>
                    <p className="text-xs text-[#94A3B8] mb-2">
                      Required: {ex.required_permissions.join(', ')}
                    </p>
                    <a
                      href={ex.docs_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#2F6BFF] flex items-center gap-1 hover:underline"
                    >
                      API Setup Guide <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default APIKeyManager;
